import styled, { css } from 'styled-components';
import { Virtuoso } from 'react-virtuoso';
import ButtonMenu from 'components/ButtonMenu';
import Badge from 'components/Badge';
import UserAvatar from 'components/UserAvatar';
import WhatsAppIcon from 'components/icons/whatsappIcon';
import { ChannelType } from 'components/ChannelPicker/types';
import { color } from 'theme';
import {
    GREY_600, GREY_800, GREEN_600, HIGHLIGHT, PURPLE_100, PURPLE_500, TEAL_600, WHITE,
} from '../../constants';

export const NotificationContainer = styled.li<{ isSelected?: boolean; isUnseen?: boolean }>`
    display: flex;
    padding: 1rem 2.5rem 1rem 1.5rem;
    align-items: flex-start;
    gap: 0.625rem;
    align-self: stretch;
    background: ${WHITE};
    cursor: pointer;
    position: relative;

    ${(props) => props.isSelected && css`
        background: ${HIGHLIGHT};
    `};

    ${(props) => props.isUnseen && css`
        &::before {
            content: '';
            position: absolute;
            left: 0.6125rem;
            top: 2.25rem;
            transform: translateY(-50%);
            width: 0.5rem;
            height: 0.5rem;
            background: ${color('error.main')};
            border-radius: 100%;
        }
    `};

    &:hover {
        background: ${HIGHLIGHT};
    }
`;

export const NotificationContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    align-self: stretch;
    justify-content: center;
    position: relative;
`;

export const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
    gap: 1rem;
`;

export const ButtonMenuWrapper = styled.div`
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
`;

export const StyledButtonMenu = styled(ButtonMenu)`
    flex-shrink: 0;

    button {
        width: 1.5rem;
        height: 1.5rem;
    }

    ${(props) => props.buttonSize === 'small' && css`
        width: 1.5rem;
        height: 1.5rem;

        svg {
            width: 1rem !important;
            height: 1rem !important;
            font-size: 1rem !important;
        }
    `};
`;

export const MessageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: relative;
    z-index: 11;
`;

export const LatestOpportunityStageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.25rem;
    min-width: 0;

    &:empty {
        display: none;
        margin-top: 0;
    }
`;

export const StyledLatestOpportunityStageBadge = styled(Badge)<{ color: string }>`
    color: ${(props) => props.color};
    background: ${(props) => `${props.color}29`};
    border-radius: 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1.125rem;
    padding: 0.125rem 0.375rem;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: capitalize;
`;

export const NotificationTitle = styled.h4`
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.375rem;
    display: flex;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    & > span {
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const PaidLabel = styled.span`
    flex-shrink: 0;
    padding: 0.125rem 0.375rem;
    border-radius: 0.375rem;
    background: ${PURPLE_100};
    color: ${PURPLE_500};
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1.125rem;
`;

export const NotificationText = styled.p<{ hasIcon?: boolean }>`
    display: flex;
    flex: 1;
    font-size: 0.8125rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.25rem;
    max-height: 1.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    margin-right: 3rem;

    ${(props) => props.hasIcon && css`
        margin-left: 0.25rem;
    `};
`;

export const AIStatusIconWrapper = styled.div<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
    filter: ${(props) => (props.isActive ? 'none' : 'grayscale(100%)')};
    opacity: ${(props) => (props.isActive ? 1 : 0.6)};
    position: relative;
    z-index: 12;

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

export const TooltipContent = styled.div`
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    white-space: nowrap;
`;

export const DateLabel = styled.div`
    font-size: 0.8125rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.25rem;
    text-transform: capitalize;
`;

export const EmptyMessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;

    > div {
        padding: 0;
        padding-bottom: 0.5rem;
    }
`;

export const EmptyMessageTitle = styled.h3`
    font-size: 1rem;
    color: ${GREY_800};
    font-weight: 500;
`;

export const EmptyMessageEmoji = styled.span`
    font-size: 2.5rem;
`;

export const NotificationsContainer = styled.div`
    width: 100%;
    height: 100%;
`;

export const StyledVirtuoso = styled(Virtuoso)`
    width: 100%;
    height: 100%;
`;

export const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

export const AvatarWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

export const StyledUserAvatar = styled(UserAvatar)<{ channel?: ChannelType }>`
    border: 4px solid #7367F0;
    svg {
        width: 2.5rem;
        height: 2.5rem;
    }

    ${(props) => props.channel === 'facebook' && css`
        border-color: #1877F2;
    `};
    
    ${(props) => props.channel === 'instagram' && css`
        border-color: #DD2A7B;
    `};

    ${(props) => props.channel === 'whatsapp' && css`
        border-color: #25D366;
    `};

    ${(props) => props.channel === 'tiktok' && css`
        border-color: #000000;
    `};

    ${(props) => props.channel === 'telegram' && css`
        border-color: #24A1DE;
    `};

    ${(props) => props.channel === 'widget_chatbot' && css`
        border-color: #2E2960;
    `};
`;

/**
 * Normalizes channel mark dimensions (SVGs vary by asset: 16×16 vs 24×24, etc.).
 * Sizes align with `StyledChannelIcon` inner `svg` rules.
 */
export const ChannelIconGlyph = styled.span<{ $size: 'small' | 'medium' | 'large' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    flex-shrink: 0;

    & > svg {
        display: block;
    }

    ${(props) => props.$size === 'small' && css`
        width: 1rem;
        height: 1rem;

        & > svg {
            width: 1rem !important;
            height: 1rem !important;
        }
    `}

    ${(props) => props.$size === 'medium' && css`
        width: 1.25rem;
        height: 1.25rem;

        & > svg {
            width: 1.25rem !important;
            height: 1.25rem !important;
        }
    `}

    ${(props) => props.$size === 'large' && css`
        width: 1.5rem;
        height: 1.5rem;

        & > svg {
            width: 1.5rem !important;
            height: 1.5rem !important;
        }
    `}
`;

export const StyledWhatsAppChannelIcon = styled(WhatsAppIcon)`
    color: #25d366;
`;

interface StyledChannelIconProps {
    size: 'small' | 'medium' | 'large';
    channel?: ChannelType;
}

export const StyledChannelIcon = styled.div<StyledChannelIconProps>`
    position: absolute;
    bottom: -2px;
    right: -2px;
    background: #7367F0;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid #7367F0;
    color: ${WHITE};

    ${(props) => props.channel === 'facebook' && css`
        background: #1877F2;
        border-color: #1877F2;
    `};
    
    ${(props) => props.channel === 'instagram' && css`
        background: #DD2A7B;
        border-color: #DD2A7B;
    `};

    ${(props) => props.channel === 'whatsapp' && css`
        background: #25D366;
        border-color: #25D366;

        svg {
            color: ${WHITE} !important;
        }
    `};

    ${(props) => props.channel === 'tiktok' && css`
        background: #000000;
        border-color: #000000;
    `};

    ${(props) => props.channel === 'telegram' && css`
        background: #24A1DE;
        border-color: #24A1DE;
    `};

    ${(props) => props.channel === 'widget_chatbot' && css`
        background: #2E2960;
        border-color: #2E2960;
    `};

    ${(props) => props.channel === 'voip' && css`
        background: ${GREEN_600};
        border-color: ${GREEN_600};
    `};

    ${(props) => props.channel === 'console' && css`
        background: ${GREY_600};
        border-color: ${GREY_600};
    `};

    ${(props) => props.channel === 'api' && css`
        background: ${TEAL_600};
        border-color: ${TEAL_600};
    `};

    ${(props) => props.size === 'small' && css`
        width: 1.25rem;
        height: 1.25rem;
        
        svg {
            width: 1rem !important;
            height: 1rem !important;
        }
    `}

    ${(props) => props.size === 'medium' && css`
        width: 1.5rem;
        height: 1.5rem;
        
        svg {
            width: 1.25rem !important;
            height: 1.25rem !important;
        }
    `}

    ${(props) => props.size === 'large' && css`
        width: 1.75rem;
        height: 1.75rem;
        
        svg {
            width: 1.5rem !important;
            height: 1.5rem !important;
        }
    `}
`;
